import React, { useState, useEffect, useCallback, Suspense, lazy } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import './App.css';

// Firebase imports
import { auth, db } from './firebase';
import { onAuthStateChanged, signOut } from "firebase/auth";
import { collection, query, where, onSnapshot, doc } from "firebase/firestore";

// Component imports
import Login from './components/Login';
import Dashboard from './Dashboard';

// --- NEW: LAZY IMPORT FOR PUBLIC VIEW ---
const PointsTable = lazy(() => import('./components/PointsTable'));

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [vehicles, setVehicles] = useState([]);

  // --- NEW: DETECT PUBLIC ROUTE ---
  const isPublicStandings = window.location.pathname === '/standings';

  // --- LOGOUT UTILITY ---
  const handleLogout = useCallback((reason = "") => {
    signOut(auth).then(() => {
      if (reason && typeof reason === 'string') alert(reason);
    });
  }, []);

  useEffect(() => {
    let timer;
    let unsubscribeVehicles = null;
    let unsubscribeProfile = null;

    // --- SECURITY LOGIC: Inactivity Auto-Logout ---
    const resetTimer = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        if (auth.currentUser) {
          handleLogout("Session expired due to inactivity. Please log in again.");
        }
      }, 900000); // 15 Minutes
    };

    // --- MAIN AUTH OBSERVER ---
    const unsubscribeAuth = onAuthStateChanged(auth, async (firebaseUser) => {
      setLoading(true);

      // Check for emailVerified to enforce registration flow for all users
      if (firebaseUser && firebaseUser.emailVerified) {
        // 1. Setup Real-time Profile Listener (Crucial for "Profile Completion" lock)
        unsubscribeProfile = onSnapshot(doc(db, "competitors", firebaseUser.uid), (docSnap) => {
          const userData = docSnap.exists() ? docSnap.data() : {};
          
          setUser({
            uid: firebaseUser.uid,
            email: firebaseUser.email,
            role: userData.role || 'competitor',
            profileCompleted: userData.profileCompleted || false, // The lock flag
            ...userData
          });

          // Once profile data is in, we can stop the initial loading screen
          setLoading(false);
        }, (error) => {
          console.error("Profile Sync Error:", error);
          setLoading(false);
        });

        // 2. Start Inactivity Tracking
        resetTimer();
        const events = ["mousemove", "mousedown", "keypress", "touchstart", "scroll"];
        events.forEach(ev => window.addEventListener(ev, resetTimer));

        // 3. Start Vehicle Listener
        const q = query(
          collection(db, "vehicles"), 
          where("ownerId", "==", firebaseUser.uid)
        );

        unsubscribeVehicles = onSnapshot(q, (snapshot) => {
          const vehicleData = snapshot.docs
            .map(doc => ({ id: doc.id, ...doc.data() }))
            .filter(v => v.deleted !== true); 
          
          setVehicles(vehicleData);
        }, (error) => {
          console.error("Vehicle Sync Error:", error);
        });

      } else {
        // User logged out or email not verified
        setUser(null);
        setVehicles([]);
        setLoading(false);
        
        if (unsubscribeVehicles) unsubscribeVehicles();
        if (unsubscribeProfile) unsubscribeProfile();
        const events = ["mousemove", "mousedown", "keypress", "touchstart", "scroll"];
        events.forEach(ev => window.removeEventListener(ev, resetTimer));
      }
    });

    return () => {
      unsubscribeAuth();
      if (unsubscribeVehicles) unsubscribeVehicles();
      if (unsubscribeProfile) unsubscribeProfile();
      if (timer) clearTimeout(timer);
      const events = ["mousemove", "mousedown", "keypress", "touchstart", "scroll"];
      events.forEach(ev => window.removeEventListener(ev, resetTimer));
    };
  }, [handleLogout]);

  // --- BRANDED LOADING SCREEN ---
  if (loading) {
    return (
      <div className="vh-100 d-flex flex-column align-items-center justify-content-center bg-dark text-white theme-poppins">
        <div className="spinner-border text-danger mb-3" role="status" style={{ width: '3.5rem', height: '3.5rem', borderWidth: '0.25em' }}>
          <span className="visually-hidden">Loading...</span>
        </div>
        <h6 className="fw-bold text-uppercase tracking-widest" style={{ letterSpacing: '2px' }}>Verifying Grid Position...</h6>
        <small className="text-white-50 mt-2">Connecting to AMSL Cloud Services</small>
      </div>
    );
  }

  return (
    <div className="App bg-white">
      {/* --- NEW: RENDER LOGIC FOR PUBLIC STANDINGS --- */}
      {isPublicStandings ? (
        <Suspense fallback={
          <div className="vh-100 d-flex justify-content-center align-items-center bg-dark text-white">
            <div className="spinner-border text-danger" role="status"></div>
          </div>
        }>
          <PointsTable isPublicView={true} />
        </Suspense>
      ) : (
        /* --- STANDARD AUTHENTICATION FLOW --- */
        !user ? (
          <Login />
        ) : (
          <Dashboard 
            user={user} 
            vehicles={vehicles} 
            // Unified Lock: Applies to any user with role 'competitor' who hasn't finished the passport
            forceProfile={user.role === 'competitor' && !user.profileCompleted} 
          />
        )
      )}
    </div>
  );
}

export default App;